import { useState, useEffect, useRef } from 'react'
import { Card, CardHeader, CardTitle, CardDescription, CardContent, Button, Input, Label, Textarea, Badge } from '@/components/ui'
import { ShoppingBag, Plus, Trash2, Edit2, Send, MessageCircle, Copy, Check, X, Save, ArrowLeft, ImagePlus } from 'lucide-react'
import { toast } from 'sonner'
import { useAuth } from '@/contexts/AuthContext'
import { Link } from 'react-router-dom'
import { CONTACT, telegramDM } from '@/config/contact'

interface Product {
  id: string
  name: string
  description: string
  price: string
  telegramText: string
  image?: string
  createdAt: string
}

interface ChatMessage {
  id: string
  userId: string
  username: string
  from: 'user' | 'admin'
  text: string
  timestamp: string
  read: boolean
}

interface Conversation {
  userId: string
  username: string
  lastMessage: ChatMessage
  unread: number
}

export function Shop() {
  const { user, isAdmin, isMember, isAuthenticated } = useAuth()
  const [products, setProducts] = useState<Product[]>([])
  const [loading, setLoading] = useState(true)
  const [copiedId, setCopiedId] = useState<string | null>(null)

  // Admin product form
  const [showForm, setShowForm] = useState(false)
  const [editingProduct, setEditingProduct] = useState<Product | null>(null)
  const [formName, setFormName] = useState('')
  const [formDesc, setFormDesc] = useState('')
  const [formPrice, setFormPrice] = useState('')
  const [formTelegram, setFormTelegram] = useState('')
  const [formImage, setFormImage] = useState('')

  // Chat state
  const [showChat, setShowChat] = useState(false)
  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [chatInput, setChatInput] = useState('')
  const [chatLoading, setChatLoading] = useState(false)

  // Admin chat state
  const [conversations, setConversations] = useState<Conversation[]>([])
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null)
  const [adminMessages, setAdminMessages] = useState<ChatMessage[]>([])

  const chatEndRef = useRef<HTMLDivElement>(null)
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null)

  // Fetch products
  useEffect(() => {
    fetch('/api/shop/products', { credentials: 'include' })
      .then(r => r.json())
      .then(data => { if (Array.isArray(data)) setProducts(data) })
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [])

  // Fetch chat messages (member) or conversations (admin)
  useEffect(() => {
    if (!isAuthenticated || !showChat) return

    const fetchChat = () => {
      if (isAdmin && !selectedUserId) {
        fetch('/api/chat/messages', { credentials: 'include' })
          .then(r => r.json())
          .then(data => { if (Array.isArray(data)) setConversations(data) })
          .catch(() => {})
      } else if (isAdmin && selectedUserId) {
        fetch(`/api/chat/messages?userId=${selectedUserId}`, { credentials: 'include' })
          .then(r => r.json())
          .then(data => { if (Array.isArray(data)) setAdminMessages(data) })
          .catch(() => {})
      } else {
        fetch('/api/chat/messages', { credentials: 'include' })
          .then(r => r.json())
          .then(data => {
            if (Array.isArray(data)) {
              // Check for new messages for non-admin users
              const prevUnread = new Set(messages.filter(m => !m.read).map(m => m.id))
              const newUnread = data.filter(m => !m.read && !prevUnread.has(m.id))

              if (newUnread.length > 0) {
                // New messages detected - auto-open chat
                setShowChat(true)
                // Show toast notification with icon and description
                toast(`${newUnread.length} new message${newUnread.length > 1 ? 's' : ''}`, {
                  description: `Chat panel opened with ${newUnread.length} new message${newUnread.length > 1 ? 's' : ''}`,
                })
              }

              setMessages(data)
            }
          })
          .catch(() => {})
      }
    }

    fetchChat()
    pollRef.current = setInterval(fetchChat, 5000)
    return () => { if (pollRef.current) clearInterval(pollRef.current) }
  }, [isAuthenticated, isAdmin, showChat, selectedUserId])

  // Scroll to bottom on new messages
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages, adminMessages])

  // Mark messages as read when opening chat
  useEffect(() => {
    if (!showChat || !isAuthenticated) return
    if (isAdmin && selectedUserId) {
      fetch('/api/chat/messages', {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId: selectedUserId }),
      }).catch(() => {})
    } else if (!isAdmin) {
      fetch('/api/chat/messages', {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      }).catch(() => {})
    }
  }, [showChat, selectedUserId, isAdmin, isAuthenticated])

  const copyTelegramLink = (product: Product) => {
    navigator.clipboard.writeText(telegramDM(product.telegramText))
    setCopiedId(product.id)
    setTimeout(() => setCopiedId(null), 2000)
  }

  // Product CRUD
  const resetForm = () => {
    setFormName('')
    setFormDesc('')
    setFormPrice('')
    setFormTelegram('')
    setFormImage('')
    setEditingProduct(null)
    setShowForm(false)
  }

  const openEditForm = (p: Product) => {
    setFormName(p.name)
    setFormDesc(p.description)
    setFormPrice(p.price)
    setFormTelegram(p.telegramText)
    setFormImage(p.image || '')
    setEditingProduct(p)
    setShowForm(true)
  }

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    if (file.size > 2 * 1024 * 1024) {
      toast.error('Image must be under 2MB')
      return
    }
    const reader = new FileReader()
    reader.onload = () => setFormImage(reader.result as string)
    reader.readAsDataURL(file)
  }

  const saveProduct = async () => {
    if (!formName.trim() || !formDesc.trim() || !formPrice.trim()) {
      toast.error('Fill in all required fields')
      return
    }

    const payload = {
      name: formName,
      description: formDesc,
      price: formPrice,
      telegramText: formTelegram || `Hi, I'm interested in ${formName}. Can you provide details?`,
      image: formImage,
      ...(editingProduct ? { id: editingProduct.id } : {}),
    }

    try {
      const res = await fetch('/api/shop/products', {
        method: editingProduct ? 'PUT' : 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      })
      if (!res.ok) throw new Error()

      // Refresh products
      const updated = await fetch('/api/shop/products', { credentials: 'include' })
      const data = await updated.json()
      if (Array.isArray(data)) setProducts(data)

      toast.success(editingProduct ? 'Product updated' : 'Product added')
      resetForm()
    } catch {
      toast.error('Failed to save product')
    }
  }

  const deleteProduct = async (id: string) => {
    try {
      await fetch('/api/shop/products', {
        method: 'DELETE',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id }),
      })
      setProducts(prev => prev.filter(p => p.id !== id))
      toast.success('Product deleted')
    } catch {
      toast.error('Failed to delete')
    }
  }

  // Chat
  const sendMessage = async () => {
    const text = chatInput.trim()
    if (!text) return
    setChatLoading(true)

    const payload: Record<string, string> = { text }
    if (isAdmin && selectedUserId) {
      payload.userId = selectedUserId
    }

    try {
      const res = await fetch('/api/chat/messages', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      })
      if (!res.ok) {
        const err = await res.json()
        toast.error(err.error || 'Failed to send')
        return
      }
      const msg = await res.json()
      if (isAdmin && selectedUserId) {
        setAdminMessages(prev => [...prev, msg])
      } else {
        setMessages(prev => [...prev, msg])
      }
      setChatInput('')
    } catch {
      toast.error('Failed to send message')
    } finally {
      setChatLoading(false)
    }
  }

  const currentChatMessages = isAdmin ? adminMessages : messages

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="space-y-2">
        <div className="flex items-center gap-3">
          <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-primary/20 to-purple-500/20">
            <ShoppingBag className="h-6 w-6 text-primary" />
          </div>
          <div>
            <h1 className="text-2xl font-bold tracking-tight">Shop</h1>
            <p className="text-muted-foreground">Services, products, and direct contact</p>
          </div>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-[1fr_380px]">
        {/* Left: Products */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold">Products & Services</h2>
            {isAdmin && (
              <Button size="sm" onClick={() => { resetForm(); setShowForm(true) }}>
                <Plus className="h-4 w-4 mr-1.5" />
                Add Product
              </Button>
            )}
          </div>

          {/* Admin product form */}
          {isAdmin && showForm && (
            <Card className="glass border-primary/30">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base">
                    {editingProduct ? 'Edit Product' : 'New Product'}
                  </CardTitle>
                  <Button variant="ghost" size="icon" className="h-7 w-7" onClick={resetForm}>
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="space-y-1.5">
                  <Label>Name *</Label>
                  <Input value={formName} onChange={e => setFormName(e.target.value)} placeholder="Product name" />
                </div>
                <div className="space-y-1.5">
                  <Label>Description *</Label>
                  <Textarea
                    value={formDesc}
                    onChange={e => setFormDesc(e.target.value)}
                    placeholder="Product description"
                    className="min-h-[80px] resize-none"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label>Price *</Label>
                    <Input value={formPrice} onChange={e => setFormPrice(e.target.value)} placeholder="e.g. $50" />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label>Telegram Message (optional)</Label>
                  <Input
                    value={formTelegram}
                    onChange={e => setFormTelegram(e.target.value)}
                    placeholder="Pre-filled message for Telegram"
                  />
                  <p className="text-xs text-muted-foreground">
                    Leave blank for auto-generated message
                  </p>
                </div>
                <div className="space-y-1.5">
                  <Label>Image (optional)</Label>
                  {formImage ? (
                    <div className="relative">
                      <img src={formImage} alt="Preview" className="w-full h-32 object-cover rounded-md border border-border" />
                      <Button
                        variant="ghost"
                        size="icon"
                        className="absolute top-1 right-1 h-7 w-7 bg-background/80 backdrop-blur-sm"
                        onClick={() => setFormImage('')}
                      >
                        <X className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  ) : (
                    <label className="flex flex-col items-center gap-1.5 rounded-md border-2 border-dashed border-border p-4 cursor-pointer hover:border-primary/50 transition-colors">
                      <ImagePlus className="h-6 w-6 text-muted-foreground" />
                      <span className="text-xs text-muted-foreground">Upload image (max 2MB)</span>
                      <input type="file" accept="image/*" className="hidden" onChange={handleImageUpload} />
                    </label>
                  )}
                </div>
                <Button onClick={saveProduct} className="w-full">
                  <Save className="h-4 w-4 mr-1.5" />
                  {editingProduct ? 'Update' : 'Add'} Product
                </Button>
              </CardContent>
            </Card>
          )}

          {/* Products grid */}
          {loading ? (
            <div className="text-center py-8 text-muted-foreground">Loading products...</div>
          ) : products.length === 0 ? (
            <Card className="glass">
              <CardContent className="py-12 text-center text-muted-foreground">
                <ShoppingBag className="h-12 w-12 mx-auto mb-4 opacity-30" />
                <p>No products yet.</p>
                {isAdmin && <p className="text-sm mt-1">Click "Add Product" to create one.</p>}
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4 sm:grid-cols-2">
              {products.map(product => (
                <Card key={product.id} className="glass transition-all hover:border-primary/50 overflow-hidden">
                  {product.image && (
                    <div className="w-full h-36 overflow-hidden border-b border-border">
                      <img src={product.image} alt={product.name} className="w-full h-full object-cover" />
                    </div>
                  )}
                  <CardHeader className="pb-3">
                    <div className="flex items-start justify-between gap-2">
                      <CardTitle className="text-base">{product.name}</CardTitle>
                      <Badge variant="secondary" className="text-xs shrink-0">{product.price}</Badge>
                    </div>
                    <CardDescription>{product.description}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="flex gap-2">
                      <a
                        href={telegramDM(product.telegramText)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex-1"
                      >
                        <Button variant="outline" size="sm" className="w-full">
                          <Send className="h-3.5 w-3.5 mr-1.5" />
                          Telegram
                        </Button>
                      </a>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => copyTelegramLink(product)}
                        title="Copy Telegram link"
                      >
                        {copiedId === product.id ? (
                          <Check className="h-3.5 w-3.5 text-green-400" />
                        ) : (
                          <Copy className="h-3.5 w-3.5" />
                        )}
                      </Button>
                      {isAdmin && (
                        <>
                          <Button variant="ghost" size="sm" onClick={() => openEditForm(product)}>
                            <Edit2 className="h-3.5 w-3.5" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => deleteProduct(product.id)}>
                            <Trash2 className="h-3.5 w-3.5 text-red-400" />
                          </Button>
                        </>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          {/* Contact via Telegram */}
          <a
            href={telegramDM('Hi, I have a question about your services.')}
            target="_blank"
            rel="noopener noreferrer"
            className="block"
          >
            <Card className="glass border-[#0088cc]/30 transition-all hover:border-[#0088cc]/60 cursor-pointer">
              <CardContent className="flex items-center gap-4 p-4">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-[#0088cc]/20 shrink-0">
                  <Send className="h-5 w-5 text-[#0088cc]" />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium">Direct Telegram Contact</p>
                  <p className="text-xs text-muted-foreground">Message @{CONTACT.telegram.username} on Telegram</p>
                </div>
              </CardContent>
            </Card>
          </a>
        </div>

        {/* Right: Chat */}
        <div className="space-y-4">
          <Card className="glass h-full flex flex-col">
            <CardHeader className="pb-3 border-b border-border shrink-0">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  {isAdmin && selectedUserId && (
                    <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setSelectedUserId(null)}>
                      <ArrowLeft className="h-4 w-4" />
                    </Button>
                  )}
                  <div className="flex items-center gap-2">
                    <MessageCircle className="h-5 w-5 text-primary" />
                    {isAdmin && (
                      <Badge variant="secondary" className="text-xs">
                        New: {conversations.reduce((acc, c) => acc + (c.unread || 0), 0)}
                      </Badge>
                    )}
                  </div>
                  <CardTitle className="text-base">
                    {isAdmin
                      ? selectedUserId
                        ? conversations.find(c => c.userId === selectedUserId)?.username || 'Chat'
                        : 'Conversations'
                      : 'Chat with Admin'}
                  </CardTitle>
                </div>
                {!showChat && (
                  <Button size="sm" variant="outline" onClick={() => setShowChat(true)}>
                    Open Chat
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent className="flex-1 flex flex-col p-0 min-h-[400px]">
              {!isAuthenticated ? (
                <div className="flex-1 flex flex-col items-center justify-center p-6 text-center">
                  <MessageCircle className="h-10 w-10 text-muted-foreground/30 mb-3" />
                  <p className="text-sm text-muted-foreground mb-3">Sign in to chat with us</p>
                  <Link to="/auth">
                    <Button size="sm">Sign In</Button>
                  </Link>
                </div>
              ) : !showChat ? (
                <div className="flex-1 flex flex-col items-center justify-center p-6 text-center">
                  <MessageCircle className="h-10 w-10 text-muted-foreground/30 mb-3" />
                  <p className="text-sm text-muted-foreground">Chat panel opens automatically when you receive new messages</p>
                </div>
              ) : isAdmin && !selectedUserId ? (
                // Admin: conversation list
                <div className="flex-1 overflow-y-auto">
                  {conversations.length === 0 ? (
                    <div className="flex-1 flex items-center justify-center p-6">
                      <p className="text-sm text-muted-foreground">No conversations yet</p>
                    </div>
                  ) : (
                    <div className="divide-y divide-border">
                      {conversations.map(conv => (
                        <button
                          key={conv.userId}
                          onClick={() => setSelectedUserId(conv.userId)}
                          className="w-full text-left p-4 hover:bg-accent/50 transition-colors"
                        >
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-sm font-medium">{conv.username}</span>
                            <div className="flex items-center gap-2">
                              {conv.unread > 0 && (
                                <Badge className="bg-primary text-xs h-5 min-w-5 flex items-center justify-center">
                                  {conv.unread}
                                </Badge>
                              )}
                              <span className="text-xs text-muted-foreground">
                                {new Date(conv.lastMessage.timestamp).toLocaleDateString()}
                              </span>
                            </div>
                          </div>
                          <p className="text-xs text-muted-foreground truncate">
                            {conv.lastMessage.from === 'admin' ? 'You: ' : ''}{conv.lastMessage.text}
                          </p>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                // Chat messages view
                <>
                  <div className="flex-1 overflow-y-auto p-4 space-y-3 custom-scrollbar">
                    {currentChatMessages.length === 0 ? (
                      <div className="text-center py-8">
                        <p className="text-sm text-muted-foreground">No messages yet. Say hello!</p>
                      </div>
                    ) : (
                      currentChatMessages.map(msg => (
                        <div
                          key={msg.id}
                          className={`flex ${
                            (isAdmin ? msg.from === 'admin' : msg.from === 'user')
                              ? 'justify-end'
                              : 'justify-start'
                          }`}
                        >
                          <div
                            className={`max-w-[80%] rounded-lg px-3 py-2 ${
                              (isAdmin ? msg.from === 'admin' : msg.from === 'user')
                                ? 'bg-primary text-primary-foreground'
                                : 'bg-muted'
                            }`}
                          >
                            {isAdmin && msg.from === 'user' && (
                              <p className="text-xs font-medium mb-0.5 opacity-70">{msg.username}</p>
                            )}
                            <p className="text-sm whitespace-pre-wrap break-words">{msg.text}</p>
                            <p className={`text-[10px] mt-1 ${
                              (isAdmin ? msg.from === 'admin' : msg.from === 'user')
                                ? 'text-primary-foreground/60'
                                : 'text-muted-foreground'
                            }`}>
                              {new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                            </p>
                          </div>
                        </div>
                      ))
                    )}
                    <div ref={chatEndRef} />
                  </div>

                  {/* Chat input */}
                  <div className="border-t border-border p-3 shrink-0">
                    <div className="flex gap-2">
                      <Input
                        value={chatInput}
                        onChange={e => setChatInput(e.target.value)}
                        placeholder="Type a message..."
                        className="flex-1"
                        onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage() } }}
                        disabled={chatLoading}
                      />
                      <Button
                        size="icon"
                        onClick={sendMessage}
                        disabled={chatLoading || !chatInput.trim()}
                      >
                        <Send className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  )
}
